import { useQuery } from "@apollo/client/react";
import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import { Container, Content, Loader } from "@volumental/common";
import { useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { graphql } from "../../api/gql";
import { BrandingFooter, Header } from "../../components/accessible-components";
import { useSessionStore } from "../../store";
import { createSizeChartData, getProductName } from "../../util/utils";
import { GeneralErrorPage } from "../error/GeneralErrorPage";
import { ProductDetails } from "../result/components/product-details";
import { SizeChartFeedbackController } from "./components/feedback-controller";
import { NpsBox } from "./components/nps-box";
import { ScannerController } from "./components/scanner-controller";
import { SizeChart } from "./components/size-chart";

const GetSizeChartConfigurationQuery = graphql(`
  query getSizeChartConfiguration($productID: String!, $genderAge: String, $brandName: String, $chartId: String) {
    configuration {
      unitSystem
    }
    ...SizeChartQueryFragment
    ...StyleMetaDataQueryFragment
  }
`);

interface SizeChartViewProps {
  onClose: () => void;
}
export function SizeChartView({ onClose }: SizeChartViewProps): JSX.Element {
  const { t } = useTranslation();
  const { context, productID, genderAge, brandName, chartId } = useSessionStore((store) => store);
  const { loading, error, data } = useQuery(GetSizeChartConfigurationQuery, {
    variables: {
      productID: productID ?? "",
      genderAge,
      brandName,
      chartId,
    },
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const onCloseSideBar = (): void => {
    usageTracker.track(UsageTrackerEvent.CloseSizeChart);
    onClose();
  };

  if (loading) {
    return <Loader />;
  }

  if (!productID) {
    return <GeneralErrorPage errorText="Missing product id" onClose={onClose} />;
  }
  if (error || !data) {
    return <GeneralErrorPage errorText="Missing SizeChartView data" onClose={close} />;
  }

  const sizeChart = createSizeChartData(data.sizeChart, data.styleMetaData);
  if (!sizeChart) {
    return <GeneralErrorPage errorText="Missing size chart" onClose={close} />;
  }
  const productName = getProductName(sizeChart.shoe, sizeChart.predictorLevel);

  return (
    <Container>
      <Header close={onCloseSideBar} />
      <Content
        onScroll={() => {
          if (!hasScrolled) {
            setHasScrolled(true);
            usageTracker.track(UsageTrackerEvent.SizeChartScroll);
          }
        }}
      >
        <ProductDetails
          imageSrc={sizeChart.shoe.imageURL ?? undefined}
          displayName={`${t("for")} ${productName}`}
          title={t("sizeGuide")}
          mode="sizeguide"
        />
        <ScannerController />
        <SizeChart sizeChart={sizeChart} unitSystem={data.configuration.unitSystem} />
        <NpsBox
          onShowModal={() => {
            setShowFeedback(true);
          }}
        />
        <div className="pt-4">
          <BrandingFooter limitedBranding={context === "shopify"} />
        </div>
      </Content>
      {Boolean(showFeedback) && (
        <SizeChartFeedbackController
          onClose={() => {
            setShowFeedback(false);
          }}
        />
      )}
    </Container>
  );
}
